// 导入主题配置和UI组件
import { initOrtEnvironment, getDefaultOrtWasmPaths, SceneFS } from 'visionary-core'

import { LangTools } from '@/uiControls/lang-tools.ts'
import { ThemeController } from '@/visionaryControllers/themeController.ts';
import { ToolbarNavTools } from '@/uiControls/toolbarNav-tools.ts';

import { MainViewTools } from '@/uiControls/main-view-tools.ts'
import { RightPanelTools } from '@/uiControls/right-panel-tools.ts'
import { TimelinePanelTools } from '@/uiControls/panelControls/timeline-panel-tools.ts'
import '@/uiControls/styles/common.scss'
import { globalManager } from '@/utils/globalConfig.ts'
import { modelDialogTools, globalTools, initUIEventListener } from '@/utils/globalConfig.ts'
import { getI18nText } from '@/utils/commonFunc.ts';

function initVisionary() {
    const initializing = getI18nText('loadingMessage.initializing');
    modelDialogTools.showLoadingInfoModal(initializing);
    // 初始化 ONNX Runtime 环境，路径相对当前 HTML 页面
    const wasmPaths = getDefaultOrtWasmPaths();
    console.log(`[Editor] Initializing ONNX Runtime with paths: ${wasmPaths}`);
    initOrtEnvironment(wasmPaths);

    // 初始化主题控制器
    // globalTools.themeController = new ThemeController('themeSelector');

    // 初始化主视图（提供模型加载渲染能力）
    globalTools.mainViewTools = new MainViewTools('mainViewContainer');

    // 创建 SceneFS，并注入到 ToolbarNavTools；同时注入 getApp 适配器（最小实现）
    const sceneFS = new SceneFS();
    const getApp = () => {
        // 双视窗适配：将加载与变换分发到左右两个 VisionaryScene
        const leftSceneTools = globalTools.mainViewTools?.getLeftSceneViewTools?.();
        const rightSceneTools = globalTools.mainViewTools?.getRightSceneViewTools?.();
        const leftVScene = leftSceneTools?.getSceneView?.();
        const rightVScene = rightSceneTools?.getSceneView?.();
        const leftAnimController = leftSceneTools?.getAnimationController?.();
        const rightAnimController = rightSceneTools?.getAnimationController?.();

        const pickView = (view) => view === 'right' ? rightVScene : leftVScene;
        const pickAnimationController = (view) => view === 'right' ? rightAnimController : leftAnimController;

        // 注册名称集合（可选，用于 getModels）
        const registry = new Set();

        const setByNameInView = (view, name, updater) => {
            const vscene = pickView(view);
            if (!vscene || typeof vscene.getObjects !== 'function') return;
            const objs = vscene.getObjects() || [];
            for (const vo of objs) {
                const fileName = vo?.file?.name;
                const candidates = [vo?.id, fileName, vo?.name].filter(Boolean);
                if (candidates.includes(name)) {
                    updater(vo);
                }
            }
        };

        return {
            // 旧接口（assets 回退会用到）：同时加载到两侧（保持兼容）
            loadONNXModel: async (fileOrUrl, name, _static) => {
                registry.add(name);
                await leftVScene?.loadModel(fileOrUrl, { expectedType: 'onnx' });
                await rightVScene?.loadModel(fileOrUrl, { expectedType: 'onnx' });
            },
            loadPLY: async (fileOrUrl, _opts) => {
                await leftVScene?.loadModel(fileOrUrl, { expectedType: 'ply' });
                await rightVScene?.loadModel(fileOrUrl, { expectedType: 'ply' });
            },

            // 新接口（scenes[] 专用）：仅加载到指定视窗
            loadONNXModelToView: async (view /* 'left'|'right' */, fileOrUrl, name, _static) => {
                registry.add(name);
                await pickView(view)?.loadModel(fileOrUrl, { expectedType: 'onnx' });
            },
            loadPLYToView: async (view /* 'left'|'right' */, fileOrUrl) => {
                await pickView(view)?.loadModel(fileOrUrl, { expectedType: 'ply' });
            },
            getSceneView: (view /* 'left'|'right' */) => pickView(view),
            getAnimationControllerForView: (view /* 'left'|'right' */) => pickAnimationController(view),
            loadSerializedFileModel: async (view /* 'left'|'right' */, fileOrUrl, modelEntry) => {
                const vscene = pickView(view);
                if (!vscene || typeof vscene.loadModel !== 'function') return;
                if (modelEntry?.name) {
                    registry.add(modelEntry.name);
                }
                const lowerName = (modelEntry?.assetName || modelEntry?.name || '').toLowerCase();
                const inferredType = modelEntry?.type
                    || (lowerName.endsWith('.onnx') ? 'onnx'
                        : (lowerName.endsWith('.ply') ? 'ply' : undefined));
                const options = {
                    expectedType: inferredType,
                    forcedId: modelEntry?.id,
                    displayName: modelEntry?.name
                };
                await vscene.loadModel(fileOrUrl, options);
                if (Array.isArray(modelEntry?.trs) && typeof vscene.applyTRSToObject === 'function') {
                    vscene.applyTRSToObject(modelEntry.id || modelEntry.name, modelEntry.trs);
                }
                if (modelEntry?.gaussianParams && typeof vscene.applyGaussianParams === 'function') {
                    vscene.applyGaussianParams(modelEntry.id || modelEntry.name, modelEntry.gaussianParams);
                }
            },

            // 变换设置（按视窗）
            getModelManagerForView: (view /* 'left'|'right' */) => ({
                setModelPosition: (id /* name */, x, y, z) => setByNameInView(view, id, (vo) => {
                    if (vo?.object?.position) vo.object.position.set(x, y, z);
                }),
                setModelRotation: (id /* name */, rx, ry, rz) => setByNameInView(view, id, (vo) => {
                    if (vo?.object?.rotation) vo.object.rotation.set(rx, ry, rz);
                }),
                setModelScale: (id /* name */, sOrVec) => setByNameInView(view, id, (vo) => {
                    if (!vo?.object?.scale) return;
                    if (Array.isArray(sOrVec)) {
                        vo.object.scale.set(sOrVec[0] ?? 1, sOrVec[1] ?? 1, sOrVec[2] ?? 1);
                    } else {
                        vo.object.scale.set(sOrVec ?? 1, sOrVec ?? 1, sOrVec ?? 1);
                    }
                })
            }),

            // 兼容接口（环境设置与摘要）
            getModels: () => Array.from(registry).map(name => ({ id: name, name })),
            setGaussianScale: (_s) => { },
            setBackgroundColor: (_rgba) => { },
            getModelManager: () => ({
                setModelPosition: (id, x, y, z) => { setByNameInView('left', id, vo => vo.object.position.set(x, y, z)); setByNameInView('right', id, vo => vo.object.position.set(x, y, z)); },
                setModelRotation: (id, rx, ry, rz) => { setByNameInView('left', id, vo => vo.object.rotation.set(rx, ry, rz)); setByNameInView('right', id, vo => vo.object.rotation.set(rx, ry, rz)); },
                setModelScale: (id, sOrVec) => { setByNameInView('left', id, vo => Array.isArray(sOrVec) ? vo.object.scale.set(sOrVec[0] ?? 1, sOrVec[1] ?? 1, sOrVec[2] ?? 1) : vo.object.scale.set(sOrVec ?? 1, sOrVec ?? 1, sOrVec ?? 1)); setByNameInView('right', id, vo => Array.isArray(sOrVec) ? vo.object.scale.set(sOrVec[0] ?? 1, sOrVec[1] ?? 1, sOrVec[2] ?? 1) : vo.object.scale.set(sOrVec ?? 1, sOrVec ?? 1, sOrVec ?? 1)); }
            })
        };
    };
    globalManager.setSceneFS(sceneFS);
    globalManager.setInjectedGetApp(getApp);

    globalTools.toolbarNavTools = new ToolbarNavTools('toolbar-nav-tools', 'toolbar-right-nav-tools');

    globalTools.rightPanelTools = new RightPanelTools('rightPanelTools');
    globalTools.timelinePanelTools = new TimelinePanelTools('timelinePanelTools');

    // 等待主题控制器初始化完成
    // if (globalTools.themeController) {
    //     // 检查主题控制器是否就绪
    //     const checkThemeController = () => {
    //         if (globalTools.themeController.isReady()) {
    //             globalTools.themeController.setTheme('dark');
    //             console.log('主题控制器已就绪');
    //             console.log('当前主题:', globalTools.themeController.getCurrentTheme().name);
    //             console.log('可用主题:', globalTools.themeController.getAvailableThemes().map(t => t.name));
    //         } else {
    //             setTimeout(checkThemeController, 100);
    //         }
    //     };
    //     checkThemeController();
    // }

    // 将 globalTools 暴露到 window 对象，供其他组件访问
    window.globalTools = globalTools;
    if (new URLSearchParams(window.location.search).get('paperTest') === '1') {
        window.globalManager = globalManager;
    }

    // 初始化按钮点击跟踪（在页面完全加载后）
    // trackButtonClick 函数由 tracking.js 提供，在 HTML 中已引入
    if (typeof window.trackButtonClick === 'function') {
        window.trackButtonClick();
    } else {
        console.warn('[Editor] trackButtonClick function not found. Make sure tracking.js is loaded.');
    }

    initUIEventListener();
    // 初始化语言控制器 - 确保所有工具（toolbarNavTools、timelinePanelTools、rightPanelTools）的 DOM 已完全创建
    // 使用 Promise.resolve().then() 确保在下一个微任务队列中执行，此时所有同步的 DOM 操作应该已经完成
    // 然后再使用 setTimeout 确保在下一个事件循环中执行，给浏览器足够的时间完成 DOM 渲染
    Promise.resolve().then(() => {
        setTimeout(() => {
            // 验证关键工具是否已初始化
            if (globalTools.toolbarNavTools &&
                globalTools.timelinePanelTools &&
                globalTools.rightPanelTools) {
                // 验证关键 DOM 元素是否存在
                const toolbarContainer = document.getElementById('toolbar-nav-tools');
                const timelineContainer = document.getElementById('timelinePanelTools');
                const rightPanelContainer = document.getElementById('rightPanelTools');

                if (toolbarContainer && timelineContainer && rightPanelContainer) {
                    console.log('[Editor] All tools initialized, initializing language controller...');
                    globalTools.langTools = new LangTools('langController');
                } else {
                    console.warn('[Editor] Some tool containers not found, retrying language controller initialization...');
                    // 如果容器不存在，再等待一段时间后重试
                    setTimeout(() => {
                        globalTools.langTools = new LangTools('langController');
                    }, 100);
                }
            } else {
                console.warn('[Editor] Some tools not initialized, retrying language controller initialization...');
                setTimeout(() => {
                    globalTools.langTools = new LangTools('langController');
                }, 100);
            }
        }, 0);
    });

}
console.log('initVisionary start', new Date().getTime());
if (import.meta.env.MODE != 'development') { // helen 2026-04-02 开发环境不显示欢迎信息和信息提示模态框
    modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.welcome'));
    modelDialogTools.initNoticeMessageModalShow();
}
// 检查 DOM 是否就绪，如果就绪则立即执行，否则等待 DOMContentLoaded；
// 如果直接用document.addEventListener('DOMContentLoaded', initApp); 会无法初始化场景，
// 因为DOMContentLoaded事件在页面加载完成后才会触发，而Three.js场景需要等待DOMContentLoaded事件触发后才能初始化
if (document.readyState === 'loading') {
    // DOM 还在加载中，等待 DOMContentLoaded 事件
    document.addEventListener('DOMContentLoaded', initVisionary);
} else {
    modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.finishLoadInit'));
    // DOM 已经加载完成，立即执行
    initVisionary();
}